import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paymentService } from '../../services/payment.service';
import { accountService, type CreditTransactionRow, type PaymentRow } from '../../services/account.service';
import { TopUpDialog, type SettleStatus } from '../landing/TopUpDialog';
import { DomainsPanel } from './DomainsPanel';
import { FONT_MONO, FONT_SANS, FONT_SERIF } from '../landing/landingData';

/**
 * Credits & billing panel (design "HyVay Account" → Credits & billing):
 * balance card with Top up, promo-code card, and the Transactions panel with
 * Credits / Payments tabs, a pending-payment banner, "Check" for pending
 * Midtrans orders, receipts, and client-side pagination.
 *
 * Two tabs because they answer different questions: "Credits" is the ledger
 * (every chat, publish, topup and redeem), while "Payments" is the money side —
 * Midtrans orders, which are the only rows a receipt can exist for.
 */

const KIND_LABEL: Record<string, string> = {
    CHAT: 'Chat AI',
    PUBLISH: 'Publish',
    VIDEO_RENDER: 'Video render',
    TOPUP: 'Top up',
    REDEEM: 'Promo code',
    BONUS: 'Top-up bonus',
    OPENING: 'Opening balance',
    ADJUSTMENT: 'Adjustment',
};

const PAGE_SIZE = 10;
const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);
const fmtCredits = (n: number) => n.toLocaleString('id-ID', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('id-ID');
const fmtIDR = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

type Tab = 'credits' | 'payments';

interface Row {
    key: string;
    date: string;
    title: string;
    desc: string;
    amount: string;
    color: string;
    pending: boolean;
    orderId: string | null;
    receiptId: string | null;
}

const segBtn = (active: boolean): React.CSSProperties => ({ height: 32, padding: '0 16px', border: 0, borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', background: active ? 'var(--hv-card)' : 'transparent', color: active ? 'var(--hv-ink)' : 'var(--hv-body)' });
const pagerBtn = (enabled: boolean): React.CSSProperties => ({ width: 32, height: 32, borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: enabled ? 'var(--hv-ink)' : 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: enabled ? 'pointer' : 'default' });

export const BillingView: React.FC = () => {
    const { refreshUser } = useAuth();
    const [credits, setCredits] = React.useState<number | null>(null);
    const [txns, setTxns] = React.useState<CreditTransactionRow[]>([]);
    const [nextCursor, setNextCursor] = React.useState<string | null>(null);
    const [payments, setPayments] = React.useState<PaymentRow[]>([]);
    const [tab, setTab] = React.useState<Tab>('credits');
    const [page, setPage] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [topUpOpen, setTopUpOpen] = React.useState(false);
    const [promo, setPromo] = React.useState('');
    const [redeeming, setRedeeming] = React.useState(false);
    const [redeemMsg, setRedeemMsg] = React.useState<{ ok: boolean; text: string } | null>(null);
    const [payMsg, setPayMsg] = React.useState<{ kind: SettleStatus; text: string } | null>(null);
    const [syncing, setSyncing] = React.useState<string | null>(null);
    const [pendingDismissed, setPendingDismissed] = React.useState(false);
    const autoSynced = React.useRef(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        const [c, t, p] = await Promise.all([accountService.getCredits(), accountService.getTransactions(), accountService.getPayments()]);
        if (c.success && c.data) setCredits(c.data.credits);
        if (t.success && t.data) { setTxns(t.data.rows); setNextCursor(t.data.nextCursor); }
        if (p.success && p.data) setPayments(p.data);
        if (!c.success) setError((c as any).message || 'Could not load billing data.');
        setLoading(false);
    }, []);

    React.useEffect(() => { void load(); }, [load]);

    /** Reconcile one PENDING payment against Midtrans. A webhook is a delivery and
     *  deliveries fail, so a paid top-up can sit at PENDING; asking is the fix. */
    const syncPayment = React.useCallback(async (orderId: string) => {
        setSyncing(orderId);
        const res = await paymentService.syncStatus(orderId);
        setSyncing(null);
        if (!res) { setPayMsg({ kind: 'FAILED', text: 'Could not check the payment status. Try again.' }); return; }
        if (res.status === 'SUCCESS') {
            setPayMsg({ kind: 'SUCCESS', text: `Payment ${orderId} confirmed. ${res.credits ?? 0} credits added.` });
            await load(); void refreshUser();
        } else if (res.status === 'FAILED') {
            setPayMsg({ kind: 'FAILED', text: `Payment ${orderId} failed or expired.` });
            await load();
        } else {
            setPayMsg({ kind: 'PENDING', text: `Payment ${orderId} is still waiting for confirmation from Midtrans.` });
        }
    }, [load, refreshUser]);

    // Once per visit, quietly reconcile anything still pending (bounded to 5).
    React.useEffect(() => {
        if (loading || autoSynced.current) return;
        const pending = payments.filter((p) => p.status === 'PENDING').slice(0, 5);
        if (pending.length === 0) return;
        autoSynced.current = true;
        void (async () => {
            let changed = false;
            for (const p of pending) {
                const res = await paymentService.syncStatus(p.orderId);
                if (res && res.status !== 'PENDING') changed = true;
            }
            if (changed) { await load(); void refreshUser(); }
        })();
    }, [loading, payments, load, refreshUser]);

    const promoReady = promo.trim().length > 2;
    const redeem = async () => {
        if (!promoReady || redeeming) return;
        setRedeeming(true);
        setRedeemMsg(null);
        const res = await accountService.redeem(promo.trim());
        if (res.success && res.data) {
            setRedeemMsg({ ok: true, text: `Code applied — ${res.data.credits} credits added to your balance.` });
            setPromo('');
            await load(); void refreshUser();
        } else {
            setRedeemMsg({ ok: false, text: (res as any).message || 'That code could not be redeemed.' });
        }
        setRedeeming(false);
    };

    const loadMoreTxns = async () => {
        if (!nextCursor || loadingMore) return;
        setLoadingMore(true);
        const t = await accountService.getTransactions(nextCursor);
        if (t.success && t.data) { setTxns((prev) => [...prev, ...t.data.rows]); setNextCursor(t.data.nextCursor); }
        setLoadingMore(false);
    };

    const rows: Row[] = tab === 'credits'
        ? txns.map((t) => ({
            key: t.id,
            date: fmtDate(t.createdAt),
            title: KIND_LABEL[t.kind] ?? t.kind,
            desc: t.description,
            amount: `${t.delta > 0 ? '+' : ''}${fmtCredits(t.delta)} credits`,
            color: t.delta > 0 ? 'var(--hv-mint-ink)' : 'var(--hv-ink)',
            pending: false,
            orderId: null,
            receiptId: t.kind === 'TOPUP' && t.refId ? t.refId : null,
        }))
        : payments.map((p) => ({
            key: p.id,
            date: fmtDate(p.createdAt),
            title: p.credits ? `Top up ${p.credits} credits` : 'Top up',
            desc: p.orderId,
            amount: `+${fmtIDR(p.amount)}`,
            color: p.status === 'PENDING' ? 'var(--hv-sand-ink)' : p.status === 'FAILED' ? 'var(--hv-muted)' : 'var(--hv-ink)',
            pending: p.status === 'PENDING',
            orderId: p.status === 'PENDING' ? p.orderId : null,
            receiptId: p.status === 'SUCCESS' ? p.orderId : null,
        }));

    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const curPage = Math.min(page, totalPages - 1);
    const pageRows = rows.slice(curPage * PAGE_SIZE, curPage * PAGE_SIZE + PAGE_SIZE);
    const canNext = curPage < totalPages - 1 || (tab === 'credits' && !!nextCursor);
    const goNext = async () => {
        if (curPage < totalPages - 1) { setPage(curPage + 1); return; }
        if (tab === 'credits' && nextCursor) { await loadMoreTxns(); setPage(curPage + 1); }
    };
    const firstPending = payments.find((p) => p.status === 'PENDING');

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18, animation: 'hvFade 0.4s ease both' }}>
                {/* balance */}
                <div style={{ padding: 28, borderRadius: 24, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', border: '1px solid color-mix(in srgb,var(--hv-mint-ink) 12%,transparent)' }}>
                    <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', opacity: 0.7 }}>CURRENT BALANCE</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: FONT_SERIF, fontSize: 72, lineHeight: 0.95, letterSpacing: '-0.02em' }}>{credits === null ? '…' : fmtCredits(credits)}</span>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.06em', opacity: 0.65 }}>CREDITS</span>
                    </div>
                    <p style={{ margin: '16px 0 0', fontSize: 13, lineHeight: 1.6, opacity: 0.82, ...tw('pretty') }}>1 credit per AI chat · 10 credits per publish. Credits aren't cash and can't be refunded or withdrawn once they're on your account.</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 22 }}>
                        <button type="button" onClick={() => setTopUpOpen(true)} className="hv-h-brand-82" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 44, padding: '0 22px', borderRadius: 999, border: 0, background: 'var(--brand,#13422C)', color: '#FFFDF7', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                            Top up credits
                        </button>
                    </div>
                </div>

                {/* promo */}
                <div style={{ padding: 24, borderRadius: 24, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 22px 44px -40px var(--hv-shadow)' }}>
                    <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-mint-ink)' }}>PROMO CODE</div>
                    <div style={{ marginTop: 8, fontFamily: FONT_SERIF, fontSize: 26, lineHeight: 1.1 }}>Got a code?</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                        <input className="hv-input" value={promo} onChange={(e) => { setPromo(e.target.value.toUpperCase()); setRedeemMsg(null); }} onKeyDown={(e) => e.key === 'Enter' && void redeem()} placeholder="HYVAY50" style={{ flex: 1, minWidth: 160, height: 44, padding: '0 16px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'var(--hv-page-2)', fontFamily: FONT_MONO, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hv-ink)', outline: 'none' }} />
                        <button type="button" onClick={() => void redeem()} disabled={!promoReady || redeeming} style={{ flex: 'none', height: 44, padding: '0 20px', borderRadius: 999, border: 0, background: promoReady ? 'var(--brand,#13422C)' : 'var(--hv-page-2)', color: promoReady ? '#FFFDF7' : 'var(--hv-muted)', fontSize: 13.5, fontWeight: 700, cursor: promoReady ? 'pointer' : 'default' }}>{redeeming ? 'Redeeming…' : 'Redeem'}</button>
                    </div>
                    {redeemMsg && <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 700, color: redeemMsg.ok ? 'var(--hv-mint-ink)' : 'var(--hv-danger)' }}>{redeemMsg.text}</div>}
                </div>
            </div>

            <DomainsPanel />

            {/* transactions */}
            <div className="hv-panel" style={{ padding: '26px 28px 12px', borderRadius: 24, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 26px 50px -44px var(--hv-shadow)', animation: 'hvFade 0.45s ease both' }}>
                {firstPending && !pendingDismissed && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px', marginBottom: 20, borderRadius: 18, background: 'var(--hv-sand)', color: 'var(--hv-sand-ink)' }}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" style={{ flex: 'none', marginTop: 1 }}><circle cx="12" cy="12" r="9" /><path d="M12 7.6v5.2M12 16.2v.2" /></svg>
                        <div style={{ flex: 1, fontSize: 13.5, lineHeight: 1.55, fontWeight: 600 }}>Payment {firstPending.orderId} is still waiting for confirmation. Credits land the moment it clears.</div>
                        <button type="button" onClick={() => setPendingDismissed(true)} aria-label="Dismiss" className="hv-h-opaque" style={{ flex: 'none', width: 24, height: 24, border: 0, background: 'transparent', color: 'var(--hv-sand-ink)', opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                        </button>
                    </div>
                )}
                {payMsg && (
                    <div role="status" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px', marginBottom: 20, borderRadius: 18, background: payMsg.kind === 'SUCCESS' ? 'var(--hv-mint)' : payMsg.kind === 'PENDING' ? 'var(--hv-sand)' : 'var(--hv-danger-bg)', color: payMsg.kind === 'SUCCESS' ? 'var(--hv-mint-ink)' : payMsg.kind === 'PENDING' ? 'var(--hv-sand-ink)' : 'var(--hv-danger)', fontSize: 13.5, lineHeight: 1.55, fontWeight: 600 }}>
                        <span style={{ flex: 1 }}>{payMsg.text}</span>
                        <button type="button" onClick={() => setPayMsg(null)} aria-label="Dismiss" style={{ flex: 'none', border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0, opacity: 0.7 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                        </button>
                    </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16 }}>
                    <div style={{ fontFamily: FONT_SERIF, fontSize: 30, lineHeight: 1.05, letterSpacing: '-0.01em' }}>Transactions</div>
                    <div style={{ flex: 1 }} />
                    <div style={{ display: 'flex', gap: 6, padding: 5, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                        <button type="button" onClick={() => { setTab('credits'); setPage(0); }} style={segBtn(tab === 'credits')}>Credits</button>
                        <button type="button" onClick={() => { setTab('payments'); setPage(0); }} style={segBtn(tab === 'payments')}>Payments</button>
                    </div>
                </div>

                <div className="hv-tx-head" style={{ display: 'grid', gridTemplateColumns: '100px minmax(0,1fr) 190px', gap: 16, marginTop: 22, padding: '0 4px 10px', borderBottom: '1px solid var(--hv-line)', fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>
                    <span>DATE</span><span>DESCRIPTION</span><span style={{ textAlign: 'right' }}>AMOUNT</span>
                </div>

                {loading ? (
                    <div style={{ padding: '28px 4px', fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>LOADING…</div>
                ) : pageRows.length === 0 ? (
                    <div style={{ padding: '28px 4px', fontSize: 13.5, color: 'var(--hv-body)' }}>{tab === 'credits' ? 'No transactions yet.' : 'No payments yet.'}</div>
                ) : pageRows.map((r) => (
                    <div key={r.key} className="hv-tx-row" style={{ display: 'grid', gridTemplateColumns: '100px minmax(0,1fr) 190px', gap: 16, alignItems: 'center', padding: '16px 4px', borderBottom: '1px solid var(--hv-line)' }}>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--hv-muted)' }}>{r.date}</span>
                        <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em' }}>{r.title}
                                {r.pending && <span style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 999, background: 'var(--hv-sand)', color: 'var(--hv-sand-ink)' }}>PENDING</span>}
                            </span>
                            <span title={r.desc} style={{ display: 'block', marginTop: 3, fontSize: 12.5, lineHeight: 1.5, color: 'var(--hv-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.desc}</span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
                            {r.orderId && (
                                <button type="button" onClick={() => void syncPayment(r.orderId!)} disabled={syncing === r.orderId} style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 5, border: 0, background: 'transparent', color: 'var(--hv-mint-ink)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 0, opacity: syncing === r.orderId ? 0.6 : 1 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" style={syncing === r.orderId ? { animation: 'hvSpin 1s linear infinite' } : undefined}><path d="M20 11A8 8 0 1 0 18 16" /><path d="M20 5v6h-6" /></svg>
                                    Check
                                </button>
                            )}
                            {r.receiptId && (
                                <Link to={`/account/billing/receipt/${r.receiptId}`} className="hv-h-underline" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 5, color: 'var(--hv-mint-ink)', fontSize: 12.5, fontWeight: 700 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" /><path d="M9 8h6M9 12h6" /></svg>
                                    Receipt
                                </Link>
                            )}
                            <span style={{ fontFamily: FONT_SANS, fontSize: 13, fontWeight: 700, color: r.color, whiteSpace: 'nowrap' }}>{r.amount}</span>
                        </span>
                    </div>
                ))}

                <div style={{ padding: '18px 4px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>{tab === 'payments' ? 'Payments are processed by Midtrans.' : ''}</span>
                    <div style={{ flex: 1 }} />
                    {(totalPages > 1 || canNext) && (
                        <>
                            <span style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>Page {curPage + 1} of {totalPages}{tab === 'credits' && nextCursor ? '+' : ''}</span>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button type="button" onClick={() => setPage(Math.max(0, curPage - 1))} disabled={curPage === 0} aria-label="Previous page" className="hv-h-page2" style={pagerBtn(curPage > 0)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5l-7 7 7 7" /></svg>
                                </button>
                                <button type="button" onClick={() => void goNext()} disabled={!canNext || loadingMore} aria-label="Next page" className="hv-h-page2" style={pagerBtn(canNext)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {error && <div role="alert" style={{ margin: '0 4px 12px', padding: '12px 14px', borderRadius: 14, background: 'var(--hv-danger-bg)', color: 'var(--hv-danger)', fontSize: 13, fontWeight: 600 }}>{error}</div>}
            </div>

            <TopUpDialog
                open={topUpOpen}
                onClose={() => setTopUpOpen(false)}
                onSettled={(status, message) => { setPayMsg({ kind: status, text: message }); void load(); if (status === 'SUCCESS') void refreshUser(); }}
            />
        </>
    );
};
